/**
 * StateSelect Component
 *
 * Searchable state/province selector using geolocation service data
 */

import { useState, useEffect, forwardRef } from 'react'

import { clsx } from 'clsx'

import { SearchableSelect, type SelectOption } from './searchable-select'
import { getStates } from '../../services/geolocation-service'

export interface StateSelectProps {
  /** Country code to load states for (e.g., "IN", "US") */
  countryCode: string
  /** Selected state code (e.g., "CA", "TX", "MH") */
  value?: string
  /** Callback when state changes - passes stateCode (taxCode for India), stateName, and full ISO identifier */
  onChange?: (stateCode: string | null, stateName: string | null, fullIdentifier?: string | null) => void
  /** Placeholder text */
  placeholder?: string
  /** Whether the field is disabled */
  disabled?: boolean
  /** Error message */
  error?: string
  /** Helper text */
  helperText?: string
  /** Custom class name */
  className?: string
  /** ID for accessibility */
  id?: string
  /** Hide the error/helper text area (when used inside FormField) */
  hideMessage?: boolean
}

// ============================================================================
// Hook
// ============================================================================

interface UseStateOptionsResult {
  options: SelectOption<string>[]
  isLoading: boolean
}

function useStateOptions(countryCode: string): UseStateOptionsResult {
  const [options, setOptions] = useState<SelectOption<string>[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!countryCode) {
      setOptions([])
      return
    }

    const loadStates = async (): Promise<void> => {
      setIsLoading(true)
      try {
        const states = await getStates(countryCode)
        const stateOptions: SelectOption<string>[] = states.map((state) => ({
          // Use taxCode as value for India (GST state code), otherwise use identifier
          /* v8 ignore next */
          value: state.taxCode ?? state.identifier,
          label: state.name,
          description: state.identifier,
          data: {
            countryCode: state.countryIdentifier,
            taxCode: state.taxCode,
            fullIdentifier: state.identifier,
          },
        }))
        stateOptions.sort((a, b) => a.label.localeCompare(b.label))
        setOptions(stateOptions)
      } catch {
        // silently ignore
        setOptions([])
      } finally {
        setIsLoading(false)
      }
    }

    void loadStates()
  }, [countryCode])

  return { options, isLoading }
}

// ============================================================================
// Option renderer
// ============================================================================

interface StateOptionRendererProps {
  option: SelectOption<string>
  isSelected: boolean
  isHighlighted: boolean
}

function StateOptionRenderer({ option, isSelected, isHighlighted }: StateOptionRendererProps): JSX.Element {
  return (
    <div
      className={clsx(
        'px-3 py-2 cursor-pointer transition-colors duration-150',
        isHighlighted && 'bg-accent',
        /* v8 ignore next */
        isSelected && 'bg-muted'
      )}
    >
      <div className="text-sm font-medium text-foreground">{option.label}</div>
      {option.description && (
        <div className="text-xs text-muted-foreground">{option.description}</div>
      )}
    </div>
  )
}

// ============================================================================
// Component
// ============================================================================

/**
 * StateSelect Component
 *
 * Features:
 * - Searchable state/province dropdown
 * - Loads states from geolocation service based on country
 * - Mobile-friendly bottom sheet
 * - Auto-loads when country changes
 *
 * @example
 * <StateSelect
 *   countryCode="IN"
 *   value={state}
 *   onChange={(code, name) => setState(code)}
 *   placeholder="Select state"
 * />
 */
export const StateSelect = forwardRef<HTMLButtonElement, StateSelectProps>(({
  countryCode,
  value,
  onChange,
  placeholder = 'Select state/province',
  disabled,
  error,
  helperText,
  className,
  id,
  hideMessage,
}, ref) => {
  const { options, isLoading } = useStateOptions(countryCode)

  const handleChange = (stateCode: string | null, option: SelectOption<string> | null): void => {
    /* v8 ignore next 3 */
    const fullIdentifier = (option?.data && typeof option.data === 'object' && 'fullIdentifier' in option.data)
      ? (option.data as { fullIdentifier?: string }).fullIdentifier
      : null
    /* v8 ignore next */
    onChange?.(stateCode, option?.label ?? null, fullIdentifier)
  }

  return (
    <SearchableSelect
      ref={ref}
      clearable
      modal
      className={className}
      disabled={(disabled ?? false) || !countryCode}
      error={error}
      helperText={helperText ?? (!countryCode ? 'Please select a country first' : undefined)}
      hideMessage={hideMessage}
      id={id}
      isLoading={isLoading}
      noOptionsText={countryCode ? 'No states found' : 'Please select a country first'}
      options={options}
      placeholder={placeholder}
      renderOption={(option, isSelected, isHighlighted) => (
        <StateOptionRenderer
          isHighlighted={isHighlighted}
          isSelected={isSelected}
          option={option}
        />
      )}
      searchPlaceholder="Search states..."
      value={value ?? null}
      onChange={handleChange}
    />
  )
})
StateSelect.displayName = 'StateSelect'

export default StateSelect
